import { useEffect, useId, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'
import s from './LegalGuaranteeNotice.module.css'

/**
 * EU harmonised notice on the legal guarantee of conformity.
 *
 * Art. 6(1)(l) + Art. 22a Directive 2011/83/EU (as amended by Directive (EU)
 * 2024/825), design fixed by Implementing Regulation (EU) 2025/1960. Mandatory
 * before the contract is concluded for B2C sales of goods from 27 Sept 2026.
 * myremi.com sells physical kits to consumers, so every path to the Shopify
 * checkout shows it. Pattern from the Commission's Practical Guidelines: a
 * sentence about the legal guarantee rights, and the harmonised notice appears
 * on the first click — used on the PDP buy box, the cart (as a legal footnote
 * above the checkout button) and in the footer. The graphic is never rendered
 * inline on those pages (legal review, 2026-09-24); `variant="inline"` stays
 * available for a dedicated page if that is ever wanted.
 *
 * The graphic is the Commission's official SVG, byte-identical, colour
 * version, in /public/legal/. It is rendered as a plain <img> so nothing can
 * crop, recolour or reflow it — only the width scales. Online the QR target
 * must also be offered as a clickable link, which NoticeLink does.
 *
 * Copy lives in locales (`LegalGuarantee`); asset paths and the link targets
 * are not copy and stay here, keyed by locale.
 */

type Locale = 'de' | 'en'

const ASSETS: Record<Locale, { src: string; linkHref: string }> = {
  de: {
    src: '/legal/eu-gewaehrleistungshinweis_de.svg',
    linkHref: 'https://europa.eu/youreurope/citizens/consumers/shopping/guarantees/index_de.htm'
  },
  en: {
    src: '/legal/eu-legal-guarantee-notice_en.svg',
    linkHref: 'https://europa.eu/youreurope/citizens/consumers/shopping/guarantees/index_en.htm'
  }
}

/** A4 in points — the SVG's intrinsic size; keeps the aspect ratio and avoids layout shift. */
const INTRINSIC = { width: 595, height: 842 }

export interface LegalGuaranteeNoticeProps {
  /** `trigger`: link-button opening the full notice in a dialog (PDP, cart, footer).
      `inline`: the full notice directly visible — currently unused, kept for a
      dedicated page. */
  variant?: 'trigger' | 'inline'
  /** Footer: no shield icon, short label, styled like the neighbouring links. */
  appearance?: 'default' | 'footer'
  className?: string
}

export default function LegalGuaranteeNotice({ variant = 'trigger', appearance = 'default', className = '' }: LegalGuaranteeNoticeProps) {
  const t = useTranslations('LegalGuarantee')
  const { locale } = useRouter()
  const assets = ASSETS[locale === 'en' ? 'en' : 'de']

  if (variant === 'inline') {
    return (
      <section
        aria-label={t('cta')}
        className={`${ s.inline } ${ className }`}
        data-testid="legal-guarantee-notice"
      >
        <NoticeImage src={assets.src} alt={t('alt')} lazy />
        <NoticeLink href={assets.linkHref} label={t('linkLabel')} />
      </section>
    )
  }

  return <TriggerVariant assets={assets} appearance={appearance} className={className} />
}

/* ── PDP variant: sentence + button → dialog with the full notice ─────────── */

function TriggerVariant({ assets, appearance, className }: { assets: { src: string; linkHref: string }; appearance: 'default' | 'footer'; className: string }) {
  const t = useTranslations('LegalGuarantee')
  const [open, setOpen] = useState(false)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const titleId = useId()

  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog == null) return
    if (open && !dialog.open) {
      dialog.showModal()
      // Focus lands inside the dialog (a11y); Esc closes via the native
      // `cancel` → `close` event, handled by onClose below.
      closeRef.current?.focus()
    }
    if (!open && dialog.open) dialog.close()
  }, [open])

  const footer = appearance === 'footer'

  return (
    <div className={`${ s.trigger } ${ className }`} data-testid="legal-guarantee-notice">
      {/* No "2 Jahre" in the visible text — next to "Mindestens 6 Monate
          benutzbar" that read like a second shelf life. The explanation is
          the graphic itself, one click away. */}
      <span className={s.lead}>
        {!footer && <ShieldIcon />}
        <button
          type="button"
          className={footer ? s.footerButton : s.button}
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          {footer ? t('footerLabel') : t('cta')}
        </button>
      </span>

      <dialog
        ref={dialogRef}
        className={s.dialog}
        aria-labelledby={titleId}
        onClose={() => setOpen(false)}
        onClick={(event) => {
          // A click on the backdrop hits the <dialog> element itself.
          if (event.target === dialogRef.current) setOpen(false)
        }}
      >
        <div className={s.dialogInner}>
          <div className={s.dialogHead}>
            <h2 id={titleId} className={s.dialogTitle}>{t('cta')}</h2>
            <button ref={closeRef} type="button" className={s.close} onClick={() => setOpen(false)} aria-label={t('close')}>
              ×
            </button>
          </div>
          {/* The SVG is only requested once the dialog has been opened. */}
          {open && <NoticeImage src={assets.src} alt={t('alt')} />}
          <NoticeLink href={assets.linkHref} label={t('linkLabel')} />
        </div>
      </dialog>
    </div>
  )
}

/* ── shared pieces ────────────────────────────────────────────────────────── */

function NoticeImage({ src, alt, lazy = false }: { src: string; alt: string; lazy?: boolean }) {
  // Plain <img>, not next/image: the official graphic must reach the browser
  // exactly as published — no resizing pipeline, no cropping, no re-encoding.
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt={alt}
      className={s.image}
      width={INTRINSIC.width}
      height={INTRINSIC.height}
      loading={lazy ? 'lazy' : 'eager'}
      decoding="async"
    />
  )
}

function NoticeLink({ href, label }: { href: string; label: string }) {
  return (
    <a className={s.link} href={href} target="_blank" rel="noopener noreferrer">
      {label}
    </a>
  )
}

function ShieldIcon() {
  // Neutral inline icon — the EU emblem exists only inside the official notice.
  return (
    <svg className={s.icon} viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        d="M10 1.5 3 4v5.2c0 4.3 3 7.6 7 9.3 4-1.7 7-5 7-9.3V4l-7-2.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="m6.8 10 2.1 2.1 4.3-4.3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
